import { ensureDataLayer } from './pushEvent';
import { sanitizeIdentityValue } from './sanitize';

/**
 * Non-PII identity attached to every usage-telemetry event. All fields are opaque ids,
 * booleans, or sales-org labels (BU names from a reporting BU path; never a team name,
 * which can contain customer names - the team travels as its `salesKey`).
 * `identityContext.ts` is the intended caller; it resolves the guest/virtual-admin
 * `user_id` policy before calling this.
 */
export interface ShiOneIdentity {
  userId?: string;
  employeeId?: string;
  isImpersonating: boolean;
  impersonatorId?: string;
  isVirtualAdmin: boolean;
  tenantId?: string;
  accountGroupId?: string;
  storefrontId?: string;
  aeId?: string;
  salesKey?: string;
  salesOrgStatus?: string;
  reportingBuId?: string;
  division?: string;
  divisionSegment?: string;
  regionName?: string;
  districtName?: string;
}

/**
 * Pushes identity into the dataLayer as persistent variables (no `event` key, so it
 * updates GTM's data model without firing a Custom Event trigger). This does not release
 * `GtmProvider` on its own: `identityContext.ts` marks identity resolved only once the
 * app reports it complete, so the container's first read sees every value. See
 * `identityReadiness.ts`.
 *
 * **Settled 2026-10-05:** the Google Tag reads its `user_properties.*` Configuration
 * settings once, at Initialization, and never re-reads them. That is why GTM waits for a
 * complete identity before loading; every in-session identity change in either app is a
 * full page load, so the container always initializes with the current values.
 *
 * Every field is pushed explicitly (to its value or `null`) rather than omitted when
 * not applicable, so a stale value from a prior identity cannot bleed into events
 * after an in-session change - the same reasoning `clickTracking.ts` documents for its
 * own dataLayer push. `null`, not `undefined`: GA4/gtag mishandles `undefined` values
 * mixed into an otherwise-populated object.
 *
 * Sanitizes every field even though callers are expected to already do so - this is
 * the low-level primitive and must not be bypassable.
 *
 * A low-level primitive: production gating and per-app field resolution live in each
 * app's `telemetryGtm` via `identityContext.ts`. Safely no-ops when `window` /
 * `dataLayer` is unavailable (SSR, tests).
 *
 * @see https://developers.google.com/tag-platform/tag-manager/datalayer
 */
export function setGtmUser(identity: ShiOneIdentity): void {
  const dataLayer = ensureDataLayer();
  if (dataLayer) {
    dataLayer.push({
      user_id: sanitizeIdentityValue('userId', identity.userId) ?? null,
      employee_id: sanitizeIdentityValue('employeeId', identity.employeeId) ?? null,
      is_impersonating: identity.isImpersonating ? 1 : 0,
      impersonator_id: sanitizeIdentityValue('impersonatorId', identity.impersonatorId) ?? null,
      is_virtual_admin: identity.isVirtualAdmin ? 1 : 0,
      tenant_id: sanitizeIdentityValue('tenantId', identity.tenantId) ?? null,
      account_group_id: sanitizeIdentityValue('accountGroupId', identity.accountGroupId) ?? null,
      storefront_id: sanitizeIdentityValue('storefrontId', identity.storefrontId) ?? null,
      ae_id: sanitizeIdentityValue('aeId', identity.aeId) ?? null,
      sales_key: sanitizeIdentityValue('salesKey', identity.salesKey) ?? null,
      sales_org_status: sanitizeIdentityValue('salesOrgStatus', identity.salesOrgStatus) ?? null,
      reporting_bu_id: sanitizeIdentityValue('reportingBuId', identity.reportingBuId) ?? null,
      division: sanitizeIdentityValue('division', identity.division) ?? null,
      division_segment: sanitizeIdentityValue('divisionSegment', identity.divisionSegment) ?? null,
      region_name: sanitizeIdentityValue('regionName', identity.regionName) ?? null,
      district_name: sanitizeIdentityValue('districtName', identity.districtName) ?? null,
    });
  }
}

/**
 * Clears every identity variable from the dataLayer (call on logout, or between
 * re-registrations). Does not release `GtmProvider`: a clear is not proof that there is no
 * user. Use `markShiOneIdentityAbsent` for that.
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/user-id
 */
export function clearGtmUser(): void {
  const dataLayer = ensureDataLayer();
  if (dataLayer) {
    dataLayer.push({
      user_id: null,
      employee_id: null,
      is_impersonating: 0,
      impersonator_id: null,
      is_virtual_admin: 0,
      tenant_id: null,
      account_group_id: null,
      storefront_id: null,
      ae_id: null,
      sales_key: null,
      sales_org_status: null,
      reporting_bu_id: null,
      division: null,
      division_segment: null,
      region_name: null,
      district_name: null,
    });
  }
}
